import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../lib/utils';
import {
  GraduationCap,
  Plus,
  Users,
  BookOpen,
  CalendarDays,
  Edit2,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import ExportButton from '../components/ExportButton';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  upcoming: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
  ongoing: 'bg-amber-100 text-amber-700 dark:bg-amber-900/20 dark:text-amber-400',
  completed: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
};

const VocationalTrainingPage = () => {
  const { hasRole } = useAuth();
  const canEdit = hasRole(['admin', 'project_manager']);

  const [activeTab, setActiveTab] = useState('sessions');
  const [categories, setCategories] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [sessionDialog, setSessionDialog] = useState(false);
  const [participantDialog, setParticipantDialog] = useState(false);
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [editingParticipant, setEditingParticipant] = useState(null);

  const [sessionForm, setSessionForm] = useState({
    title: '',
    category: '',
    description: '',
    instructor: '',
    start_date: '',
    end_date: '',
    location: '',
    max_participants: '',
    status: 'upcoming',
  });

  const [participantForm, setParticipantForm] = useState({
    session_id: '',
    student_name: '',
    phone: '',
    village: '',
    age: '',
    gender: '',
    attendance_status: 'registered',
  });

  const [categoryForm, setCategoryForm] = useState({ name: '' });

  const fetchAll = useCallback(async () => {
    try {
      const [catsRes, sessRes, partRes, sumRes] = await Promise.all([
        axios.get(`${API_URL}/api/training/categories`),
        axios.get(`${API_URL}/api/training/sessions`),
        axios.get(`${API_URL}/api/training/participants`),
        axios.get(`${API_URL}/api/training/summary`),
      ]);
      setCategories(catsRes.data);
      setSessions(sessRes.data);
      setParticipants(partRes.data);
      setSummary(sumRes.data);
    } catch (e) {
      toast.error('Failed to load training data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const resetSessionForm = () => {
    setSessionForm({
      title: '', category: '', description: '', instructor: '',
      start_date: '', end_date: '', location: '', max_participants: '', status: 'upcoming',
    });
    setEditingSession(null);
  };

  const resetParticipantForm = () => {
    setParticipantForm({
      session_id: '', student_name: '', phone: '', village: '',
      age: '', gender: '', attendance_status: 'registered',
    });
    setEditingParticipant(null);
  };

  const handleSaveSession = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...sessionForm,
        max_participants: sessionForm.max_participants ? parseInt(sessionForm.max_participants) : null,
      };
      if (editingSession) {
        await axios.put(`${API_URL}/api/training/sessions/${editingSession.id}`, payload);
        toast.success('Session updated');
      } else {
        await axios.post(`${API_URL}/api/training/sessions`, payload);
        toast.success('Session created');
      }
      setSessionDialog(false);
      resetSessionForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save session');
    }
  };

  const handleSaveParticipant = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...participantForm,
        age: participantForm.age ? parseInt(participantForm.age) : null,
      };
      if (editingParticipant) {
        await axios.put(`${API_URL}/api/training/participants/${editingParticipant.id}`, payload);
        toast.success('Student updated');
      } else {
        await axios.post(`${API_URL}/api/training/participants`, payload);
        toast.success('Student added to training');
      }
      setParticipantDialog(false);
      resetParticipantForm();
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save student');
    }
  };

  const handleSaveCategory = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/training/categories`, categoryForm);
      toast.success('Training type added');
      setCategoryDialog(false);
      setCategoryForm({ name: '' });
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add category');
    }
  };

  const handleDeleteSession = async (id) => {
    if (!window.confirm('Delete this session and all its participants?')) return;
    try {
      await axios.delete(`${API_URL}/api/training/sessions/${id}`);
      toast.success('Session deleted');
      fetchAll();
    } catch {
      toast.error('Failed to delete session');
    }
  };

  const handleDeleteParticipant = async (id) => {
    if (!window.confirm('Remove this student from the training?')) return;
    try {
      await axios.delete(`${API_URL}/api/training/participants/${id}`);
      toast.success('Student removed');
      fetchAll();
    } catch {
      toast.error('Failed to remove student');
    }
  };

  const openEditSession = (s) => {
    setEditingSession(s);
    setSessionForm({
      title: s.title || '',
      category: s.category || '',
      description: s.description || '',
      instructor: s.instructor || '',
      start_date: s.start_date || '',
      end_date: s.end_date || '',
      location: s.location || '',
      max_participants: s.max_participants ?? '',
      status: s.status || 'upcoming',
    });
    setSessionDialog(true);
  };

  const openEditParticipant = (p) => {
    setEditingParticipant(p);
    setParticipantForm({
      session_id: p.session_id || '',
      student_name: p.student_name || '',
      phone: p.phone || '',
      village: p.village || '',
      age: p.age ?? '',
      gender: p.gender || '',
      attendance_status: p.attendance_status || 'registered',
    });
    setParticipantDialog(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const sessionsByCategory = categories.map((c) => ({
    name: c.name,
    sessions: sessions.filter((s) => s.category === c.id).length,
  })).filter((c) => c.sessions > 0);

  return (
    <div className="space-y-6">
      {/* Hero */}
      <div
        className="rounded-xl bg-cover bg-center relative overflow-hidden h-48"
        style={{ backgroundImage: `url('https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1200')` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-indigo-700/90 to-blue-600/70" />
        <div className="absolute inset-0 flex items-center p-8">
          <div className="text-white">
            <div className="flex items-center gap-3 mb-2">
              <GraduationCap className="h-8 w-8" />
              <h2 className="font-heading text-3xl font-bold">Vocational Training Program</h2>
            </div>
            <p className="text-white/90 max-w-2xl">
              Equipping community members with practical skills for economic empowerment.
            </p>
          </div>
        </div>
        <div className="absolute top-4 right-4 flex gap-2">
          <ExportButton path="exports/list/training-sessions" filenameBase="suubi_training_sessions" label="Export Sessions" testId="training-sessions-export-btn" />
          <ExportButton path="exports/list/training-participants" filenameBase="suubi_training_participants" label="Export Participants" testId="training-participants-export-btn" />
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="training-summary">
        <Card className="card-hover">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/20">
              <BookOpen className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total Sessions</p>
              <p className="text-2xl font-heading font-bold">{summary?.total_sessions || 0}</p>
              <p className="text-xs text-indigo-600">{summary?.active_sessions || 0} active</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-blue-50 dark:bg-blue-900/20">
              <Users className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Students Enrolled</p>
              <p className="text-2xl font-heading font-bold">{summary?.total_participants || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-green-50 dark:bg-green-900/20">
              <CalendarDays className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Completed</p>
              <p className="text-2xl font-heading font-bold text-green-600">{summary?.completed_participants || 0}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="card-hover">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20">
              <GraduationCap className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Training Types</p>
              <p className="text-2xl font-heading font-bold">{categories.length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <TabsList>
            <TabsTrigger value="sessions" data-testid="tab-sessions">Sessions</TabsTrigger>
            <TabsTrigger value="students" data-testid="tab-students">Students</TabsTrigger>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          </TabsList>
          {canEdit && (
            <div className="flex flex-wrap gap-2">
              <Dialog open={categoryDialog} onOpenChange={setCategoryDialog}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="add-training-type-btn">
                    <Plus className="mr-1 h-4 w-4" /> Training Type
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle className="font-heading">Add Training Type</DialogTitle>
                    <DialogDescription>Create a new training category</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSaveCategory} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Category Name *</Label>
                      <Input
                        value={categoryForm.name}
                        onChange={(e) => setCategoryForm({ name: e.target.value })}
                        placeholder="e.g., Tailoring, Welding"
                        required
                        data-testid="training-type-name"
                      />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setCategoryDialog(false)}>Cancel</Button>
                      <Button type="submit">Add</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={sessionDialog} onOpenChange={(o) => { setSessionDialog(o); if (!o) resetSessionForm(); }}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="add-session-btn">
                    <Plus className="mr-1 h-4 w-4" /> Session
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading">{editingSession ? 'Edit' : 'New'} Training Session</DialogTitle>
                    <DialogDescription>Plan and track a vocational training session</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSaveSession} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                    <div className="space-y-2">
                      <Label>Title *</Label>
                      <Input value={sessionForm.title} onChange={(e) => setSessionForm({ ...sessionForm, title: e.target.value })} required data-testid="session-title" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Category *</Label>
                        <Select value={sessionForm.category} onValueChange={(v) => setSessionForm({ ...sessionForm, category: v })} required>
                          <SelectTrigger data-testid="session-category"><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            {categories.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select value={sessionForm.status} onValueChange={(v) => setSessionForm({ ...sessionForm, status: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="upcoming">Upcoming</SelectItem>
                            <SelectItem value="ongoing">Ongoing</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Date *</Label>
                        <Input type="date" value={sessionForm.start_date} onChange={(e) => setSessionForm({ ...sessionForm, start_date: e.target.value })} required />
                      </div>
                      <div className="space-y-2">
                        <Label>End Date</Label>
                        <Input type="date" value={sessionForm.end_date} onChange={(e) => setSessionForm({ ...sessionForm, end_date: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Instructor</Label>
                        <Input value={sessionForm.instructor} onChange={(e) => setSessionForm({ ...sessionForm, instructor: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Location</Label>
                        <Input value={sessionForm.location} onChange={(e) => setSessionForm({ ...sessionForm, location: e.target.value })} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Max Participants</Label>
                      <Input type="number" value={sessionForm.max_participants} onChange={(e) => setSessionForm({ ...sessionForm, max_participants: e.target.value })} />
                    </div>
                    <div className="space-y-2">
                      <Label>Description</Label>
                      <Textarea value={sessionForm.description} onChange={(e) => setSessionForm({ ...sessionForm, description: e.target.value })} rows={3} />
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setSessionDialog(false)}>Cancel</Button>
                      <Button type="submit" data-testid="session-submit-btn">{editingSession ? 'Update' : 'Create'}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>

              <Dialog open={participantDialog} onOpenChange={(o) => { setParticipantDialog(o); if (!o) resetParticipantForm(); }}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-participant-btn">
                    <UserPlus className="mr-1 h-4 w-4" /> Student
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle className="font-heading">{editingParticipant ? 'Edit' : 'Add'} Student</DialogTitle>
                    <DialogDescription>Enroll a student in a training session</DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSaveParticipant} className="space-y-4">
                    <div className="space-y-2">
                      <Label>Session *</Label>
                      <Select value={participantForm.session_id} onValueChange={(v) => setParticipantForm({ ...participantForm, session_id: v })} required>
                        <SelectTrigger data-testid="participant-session-select"><SelectValue placeholder="Choose session" /></SelectTrigger>
                        <SelectContent>
                          {sessions.map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Student Name *</Label>
                      <Input value={participantForm.student_name} onChange={(e) => setParticipantForm({ ...participantForm, student_name: e.target.value })} required data-testid="participant-name" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Phone</Label>
                        <Input value={participantForm.phone} onChange={(e) => setParticipantForm({ ...participantForm, phone: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Village</Label>
                        <Input value={participantForm.village} onChange={(e) => setParticipantForm({ ...participantForm, village: e.target.value })} />
                      </div>
                    </div>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Age</Label>
                        <Input type="number" value={participantForm.age} onChange={(e) => setParticipantForm({ ...participantForm, age: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Gender</Label>
                        <Select value={participantForm.gender} onValueChange={(v) => setParticipantForm({ ...participantForm, gender: v })}>
                          <SelectTrigger><SelectValue placeholder="Select" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="male">Male</SelectItem>
                            <SelectItem value="female">Female</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Status</Label>
                        <Select value={participantForm.attendance_status} onValueChange={(v) => setParticipantForm({ ...participantForm, attendance_status: v })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="registered">Registered</SelectItem>
                            <SelectItem value="attended">Attended</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="dropped">Dropped</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button type="button" variant="outline" onClick={() => setParticipantDialog(false)}>Cancel</Button>
                      <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="participant-submit-btn">{editingParticipant ? 'Update' : 'Add'}</Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          )}
        </div>

        {/* Sessions Tab */}
        <TabsContent value="sessions" className="mt-6">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead>Instructor</TableHead>
                    <TableHead>Participants</TableHead>
                    <TableHead>Status</TableHead>
                    {canEdit && <TableHead className="w-24"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No training sessions yet</TableCell>
                    </TableRow>
                  ) : (
                    sessions.map((s) => {
                      const catName = categories.find((c) => c.id === s.category)?.name || s.category;
                      return (
                        <TableRow key={s.id} className="table-row-hover">
                          <TableCell className="font-medium">{s.title}</TableCell>
                          <TableCell><Badge variant="outline">{catName}</Badge></TableCell>
                          <TableCell className="text-sm">
                            {formatDate(s.start_date)}{s.end_date ? ` → ${formatDate(s.end_date)}` : ''}
                          </TableCell>
                          <TableCell>{s.instructor || '-'}</TableCell>
                          <TableCell>{s.participant_count || 0}{s.max_participants ? ` / ${s.max_participants}` : ''}</TableCell>
                          <TableCell>
                            <Badge className={STATUS_COLORS[s.status] || ''}>{s.status}</Badge>
                          </TableCell>
                          {canEdit && (
                            <TableCell>
                              <div className="flex gap-1">
                                <Button size="icon" variant="ghost" onClick={() => openEditSession(s)} data-testid={`edit-session-${s.id}`}>
                                  <Edit2 className="h-4 w-4" />
                                </Button>
                                <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeleteSession(s.id)} data-testid={`delete-session-${s.id}`}>
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Students Tab */}
        <TabsContent value="students" className="mt-6">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Session</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Village</TableHead>
                    <TableHead>Age</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead>Status</TableHead>
                    {canEdit && <TableHead className="w-24"></TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {participants.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No students enrolled</TableCell>
                    </TableRow>
                  ) : (
                    participants.map((p) => (
                      <TableRow key={p.id} className="table-row-hover">
                        <TableCell className="font-medium">{p.student_name}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{p.session_title || '-'}</TableCell>
                        <TableCell>{p.phone || '-'}</TableCell>
                        <TableCell>{p.village || '-'}</TableCell>
                        <TableCell>{p.age || '-'}</TableCell>
                        <TableCell className="capitalize">{p.gender || '-'}</TableCell>
                        <TableCell><Badge variant="outline" className="capitalize">{p.attendance_status}</Badge></TableCell>
                        {canEdit && (
                          <TableCell>
                            <div className="flex gap-1">
                              <Button size="icon" variant="ghost" onClick={() => openEditParticipant(p)}>
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button size="icon" variant="ghost" className="text-destructive" onClick={() => handleDeleteParticipant(p.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="card-hover">
              <CardHeader>
                <CardTitle className="font-heading">Financial Summary</CardTitle>
                <CardDescription>Revenue & expenses from training</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-900/20">
                  <span className="text-sm text-muted-foreground">Revenue</span>
                  <span className="font-heading font-bold text-green-600">{formatCurrency(summary?.revenue || 0)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-red-50 dark:bg-red-900/20">
                  <span className="text-sm text-muted-foreground">Expenses</span>
                  <span className="font-heading font-bold text-red-600">{formatCurrency(summary?.expenses || 0)}</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-indigo-50 dark:bg-indigo-900/20">
                  <span className="text-sm text-muted-foreground">Net Income</span>
                  <span className="font-heading font-bold text-indigo-600">{formatCurrency(summary?.net_income || 0)}</span>
                </div>
              </CardContent>
            </Card>
            <Card className="card-hover">
              <CardHeader>
                <CardTitle className="font-heading">Sessions by Type</CardTitle>
                <CardDescription>Distribution of training categories</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {sessionsByCategory.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                      <BarChart data={sessionsByCategory}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                        <YAxis allowDecimals={false} />
                        <Tooltip />
                        <Bar dataKey="sessions" fill="#6366F1" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-muted-foreground">No sessions yet</div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default VocationalTrainingPage;
